<template>
  <div class="ticket-wrapper">
    <div class="ticket">
      <!-- Left section — info -->
      <div class="ticket-body">
        <div class="ticket-top">
          <div class="ticket-info">
            <div class="ticket-label">Ingresso</div>
            <div class="ticket-title">{{ config.ticket_title || 'Acesso Completo' }}</div>
          </div>
          <div class="ticket-badge">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M10 3L4.5 8.5L2 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            Cortesia
          </div>
        </div>

        <div class="ticket-details">
          <div class="ticket-detail" v-if="config.ticket_type">
            <span class="ticket-detail-label">Tipo</span>
            <span class="ticket-detail-value">{{ config.ticket_type }}</span>
          </div>
          <div class="ticket-detail">
            <span class="ticket-detail-label">Qtd</span>
            <span class="ticket-detail-value">1 ingresso</span>
          </div>
          <div class="ticket-detail" v-if="config.ticket_validity">
            <span class="ticket-detail-label">Válido até</span>
            <span class="ticket-detail-value">{{ config.ticket_validity }}</span>
          </div>
        </div>
      </div>

      <!-- Vertical tear line -->
      <div class="ticket-tear">
        <div class="ticket-tear-top"></div>
        <div class="ticket-tear-bottom"></div>
      </div>

      <!-- Right section — pricing -->
      <div class="ticket-pricing">
        <div class="price-block">
          <span class="price-original">R$ {{ config.ticket_value || '0,00' }}</span>
          <div class="price-current">
            <span class="price-currency">R$</span>
            <span class="price-value">0,00</span>
          </div>
        </div>

        <div class="price-savings" v-if="savingsText">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M12.25 3.5L8.75 1.75L1.75 4.375V8.75C1.75 9.67935 2.11875 10.5702 2.77513 11.2266C3.4315 11.8829 4.32229 12.25 5.25 12.25H8.75C9.67935 12.25 10.5702 11.8829 11.2266 11.2266C11.8829 10.5702 12.25 9.67935 12.25 8.75V3.5Z" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M5.25 8.75L8.75 5.25M8.75 8.75H8.7558V8.7558H8.75V8.75Z" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Você economiza R$ {{ savingsText }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue';

export default {
  name: 'IFakeTicket',
  props: {
    config: {
      type: Object,
      default: () => ({})
    }
  },
  setup(props) {
    const savingsText = computed(() => {
      const val = props.config.ticket_value;
      if (!val) return null;
      // Remove R$, dots, spaces — keep the formatted value as-is for display
      const cleaned = val.replace(/[rR$\s]/g, '').trim();
      if (!cleaned || cleaned === '0,00' || cleaned === '0') return null;
      return cleaned;
    });

    return { savingsText };
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,400;0,500;0,600;0,700&family=DM+Mono:wght@400;500&display=swap');

.ticket-wrapper {
  width: 100%;
  font-family: 'DM Sans', sans-serif;
  color: #2c2926;
}

.ticket {
  width: 100%;
  background: #ffffff;
  border-radius: 16px;
  display: flex;
  overflow: hidden;
  box-shadow:
    0 1px 3px rgba(44, 41, 38, 0.06),
    0 6px 20px rgba(44, 41, 38, 0.08);
  position: relative;
}

.ticket::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: 4px;
  background: linear-gradient(180deg,
    var(--button-color, #556ee6),
    color-mix(in srgb, var(--button-color, #556ee6) 70%, white),
    var(--button-color, #556ee6)
  );
  z-index: 1;
}

/* Left section — info */
.ticket-body {
  flex: 70;
  padding: 28px 32px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
}

.ticket-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 20px;
}

.ticket-info {
  flex: 1;
  min-width: 0;
}

.ticket-label {
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--button-color, #556ee6);
  margin-bottom: 6px;
}

.ticket-title {
  font-size: 1.1875rem;
  font-weight: 700;
  color: #1a1816;
  line-height: 1.35;
}

.ticket-badge {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  background: color-mix(in srgb, var(--button-color, #556ee6) 8%, white);
  border: 1px solid color-mix(in srgb, var(--button-color, #556ee6) 25%, white);
  border-radius: 20px;
  font-size: 0.6875rem;
  font-weight: 600;
  color: var(--button-color, #556ee6);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
}

.ticket-badge svg {
  flex-shrink: 0;
}

.ticket-details {
  display: flex;
  gap: 28px;
}

.ticket-detail {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.ticket-detail-label {
  font-size: 0.6875rem;
  color: #9a958e;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.ticket-detail-value {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #3d3833;
}

/* Vertical tear line */
.ticket-tear {
  position: relative;
  width: 1px;
  flex-shrink: 0;
}

.ticket-tear::before {
  content: '';
  position: absolute;
  top: 24px;
  bottom: 24px;
  left: 0;
  border-left: 2px dashed #e8e4de;
}

.ticket-tear-top,
.ticket-tear-bottom {
  position: absolute;
  left: 50%;
  width: 20px;
  height: 20px;
  background: var(--bg-color, #f4f1ec);
  border-radius: 50%;
  transform: translateX(-50%);
}

.ticket-tear-top {
  top: -10px;
}

.ticket-tear-bottom {
  bottom: -10px;
}

/* Right section — pricing */
.ticket-pricing {
  flex: 30;
  padding: 28px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  background: #fcfbfa;
  min-width: 0;
}

.price-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.price-original {
  font-family: 'DM Mono', monospace;
  font-size: 0.9375rem;
  color: #b8b2aa;
  text-decoration: line-through;
  text-decoration-color: #d4534b;
  text-decoration-thickness: 2px;
  font-weight: 400;
}

.price-current {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.price-currency {
  font-size: 1rem;
  font-weight: 600;
  color: var(--button-color, #556ee6);
}

.price-value {
  font-family: 'DM Mono', monospace;
  font-size: 2.25rem;
  font-weight: 500;
  color: var(--button-color, #556ee6);
  line-height: 1;
}

.price-savings {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  background: linear-gradient(135deg, #fef8ec, #fdf2da);
  border: 1px solid #f0ddb0;
  border-radius: 10px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #8a6d2b;
  white-space: nowrap;
}

.price-savings svg {
  flex-shrink: 0;
}

/* Responsive — stack on mobile */
@media (max-width: 600px) {
  .ticket {
    flex-direction: column;
  }

  .ticket-body {
    padding: 24px 20px 20px;
  }

  .ticket-tear {
    width: auto;
    height: 1px;
    margin: 0 20px;
  }

  .ticket-tear::before {
    top: 0;
    bottom: auto;
    left: 0;
    right: 0;
    border-left: none;
    border-top: 2px dashed #e8e4de;
  }

  .ticket-tear-top,
  .ticket-tear-bottom {
    top: 50%;
    transform: translateY(-50%);
    left: auto;
  }

  .ticket-tear-top {
    left: -30px;
    top: 50%;
    transform: translateY(-50%);
  }

  .ticket-tear-bottom {
    right: -30px;
    left: auto;
    bottom: auto;
    top: 50%;
    transform: translateY(-50%);
  }

  .ticket-pricing {
    width: 100%;
    padding: 20px;
    flex-direction: row;
    justify-content: space-between;
  }

  .price-block {
    align-items: flex-start;
  }

  .price-value {
    font-size: 1.75rem;
  }
}
</style>
